import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { interviewApi, Interview, InterviewFeedback, CriteriaScore } from '../services/recruitingApi';
import { applicationKeys } from './useApplications';

export interface InterviewCreate {
  format: string;
  interview_type?: string | null;
  title?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  timezone?: string;
  office_id?: string | null;
  room?: string | null;
  video_link?: string;
  scorecard_template_id?: string;
  instructions?: string;
  candidate_prep?: string;
  planned_questions?: { id: string; question: string; category: string; guidance: string; difficulty: string; source: string }[];
  participants?: { user_id: string; role?: string }[];
  send_candidate_invite?: boolean;
  send_self_invite?: boolean;
}

export interface InterviewUpdate {
  format?: string;
  interview_type?: string | null;
  title?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  timezone?: string;
  office_id?: string | null;
  room?: string | null;
  video_link?: string;
  status?: string;
  scorecard_template_id?: string;
  instructions?: string;
  candidate_prep?: string;
}

export interface FeedbackCreate {
  overall_rating?: number;
  recommendation?: string;
  criteria_scores?: CriteriaScore[];
  strengths?: string;
  concerns?: string;
  notes?: string;
  is_private?: boolean;
}

export interface InterviewListParams {
  search?: string;
  status?: string;
  format?: string;
  interview_type?: string;
  limit?: number;
  offset?: number;
}

export const interviewKeys = {
  all: ['interviews'] as const,
  list: (params?: InterviewListParams) => [...interviewKeys.all, 'list', params] as const,
  byApplication: (applicationId: string) => [...interviewKeys.all, 'application', applicationId] as const,
  upcoming: (params?: { user_id?: string; limit?: number }) => [...interviewKeys.all, 'upcoming', params] as const,
  detail: (id: string) => [...interviewKeys.all, 'detail', id] as const,
  feedback: (id: string) => [...interviewKeys.all, 'feedback', id] as const,
};

export function useInterviews(params?: InterviewListParams) {
  return useQuery({
    queryKey: interviewKeys.list(params),
    queryFn: () => interviewApi.listAll(params),
    placeholderData: keepPreviousData,
  });
}

export function useInterviewsByApplication(applicationId: string) {
  return useQuery({
    queryKey: interviewKeys.byApplication(applicationId),
    queryFn: () => interviewApi.listByApplication(applicationId),
    enabled: !!applicationId,
  });
}

export function useUpcomingInterviews(params?: { user_id?: string; limit?: number }) {
  return useQuery({
    queryKey: interviewKeys.upcoming(params),
    queryFn: () => interviewApi.listUpcoming(params),
  });
}

export function useInterview(id: string) {
  return useQuery({
    queryKey: interviewKeys.detail(id),
    queryFn: () => interviewApi.get(id),
    enabled: !!id,
  });
}

export function useInterviewFeedback(id: string) {
  return useQuery({
    queryKey: interviewKeys.feedback(id),
    queryFn: () => interviewApi.getFeedback(id),
    enabled: !!id,
  });
}

export function useCreateInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, data }: { applicationId: string; data: InterviewCreate }) =>
      interviewApi.create(applicationId, data),
    onSuccess: (_: Interview, { applicationId }: { applicationId: string; data: InterviewCreate }) => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.byApplication(applicationId) });
      queryClient.invalidateQueries({ queryKey: interviewKeys.upcoming() });
      queryClient.invalidateQueries({ queryKey: applicationKeys.detail(applicationId) });
    },
  });
}

export function useCreateCandidateInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, data }: { candidateId: string; data: InterviewCreate }) =>
      interviewApi.createForCandidate(candidateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.upcoming() });
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}

export function useUpdateInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: InterviewUpdate & { notify_candidate?: boolean } }) => interviewApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}

export function useCancelInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => interviewApi.cancel(id),
    onSuccess: (_: Interview, id: string) => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}

export function useCompleteInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => interviewApi.complete(id),
    onSuccess: (_: Interview, id: string) => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}

export function useSubmitFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ interviewId, data }: { interviewId: string; data: FeedbackCreate }) =>
      interviewApi.submitFeedback(interviewId, data),
    onSuccess: (_: InterviewFeedback, { interviewId }: { interviewId: string; data: FeedbackCreate }) => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.feedback(interviewId) });
      queryClient.invalidateQueries({ queryKey: interviewKeys.detail(interviewId) });
    },
  });
}

export function useSendInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => interviewApi.sendInvite(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.detail(id) });
    },
  });
}

export function useAddParticipant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ interviewId, userId, role }: { interviewId: string; userId: string; role?: string }) =>
      interviewApi.addParticipant(interviewId, userId, role),
    onSuccess: (_, { interviewId }) => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.detail(interviewId) });
    },
  });
}

export function useRemoveParticipant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ interviewId, userId }: { interviewId: string; userId: string }) =>
      interviewApi.removeParticipant(interviewId, userId),
    onSuccess: (_, { interviewId }) => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.detail(interviewId) });
    },
  });
}
